'use client'

import { BUTTON_VARIANT } from '@fukku/button/Button.types'
import { ButtonContentLink } from '@fukku/button/ContentLink'
import { Content } from 'fukku/Dialog/Content/Content'
import type { DialogAPI } from 'fukku/Dialog/Dialog'
import { Header } from 'fukku/Dialog/Header/Header'
import { SideSheet } from 'fukku/Dialog/SideSheet/SideSheet'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import dynamic from 'next/dynamic'
import { useRef } from 'react'
import { Channel } from 'types/masterData'
import { env } from 'utils/envManager'

import { dataTestIds } from '../../constants/dataTestIds/dataTestIds'
import { useAnalytics } from '../../contexts/AnalyticsProvider/AnalyticsProvider'
import { PdpActionEvents } from '../../types/PdpActionEvents'

import styles from './ShippingAndReturns.module.scss'

const DynamicShippingAndReturns = dynamic(() =>
	import(
		'shipping-and-returns/components/ShippingAndReturns/ShippingAndReturns'
	).then((mod) => mod.ShippingAndReturns)
)

interface ShippingAndReturnsProps {
	readonly className?: string
}

export function ShippingAndReturns({
	className,
}: ShippingAndReturnsProps): React.ReactNode {
	const {
		country: { hasOnlineSale },
	} = useMasterData()
	const { sendPdpActionEvent } = useAnalytics()

	const { t } = useLabels()
	const modalRef = useRef<DialogAPI>(null)

	const title = t('pdp.modal.conditions.deliveryAndReturns.title')
	const channel = env.NEXT_PUBLIC_CHANNEL as Channel.Shop | Channel.Outlet

	const onShippingAndReturnsClick = (): void => {
		modalRef.current?.show()
		sendPdpActionEvent(PdpActionEvents.SHIPPING_RETURN_INFO)
	}

	const shippingByChannel = {
		[Channel.Shop]: {
			label: t('pdp.deliveryAndReturns.link'),
		},
		[Channel.Outlet]: {
			label: t('pdp.deliveryAndReturns.link'),
		},
	}

	if (!hasOnlineSale) {
		return null
	}

	return (
		<div className={className}>
			<ButtonContentLink
				variant={BUTTON_VARIANT.DEFAULT}
				className={styles.shippingAndReturnsButton}
				onClick={onShippingAndReturnsClick}
				data-testid={dataTestIds['pdp.productInfo.shippingAndReturns']}
			>
				{shippingByChannel[channel].label}
			</ButtonContentLink>

			<SideSheet title={title} ref={modalRef}>
				<Header title={title} />
				<Content>
					<DynamicShippingAndReturns
						dataTestId={
							dataTestIds['pdp.productInfo.shippingAndReturns.content']
						}
					/>
				</Content>
			</SideSheet>
		</div>
	)
}
